import isNil from 'lodash/isNil';
import {
  forwardRef,
  type Ref,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import type { DrillMode, TNode } from '@revelio/ui';
import {
  getChildren,
  getPathToNode,
  type TreeApiBaseProps,
  type TreeApiControlProps,
  type TreeData,
  TreeSelection,
  type TreeSelectionComponentProps,
  useTreeApi,
} from '@revelio/ui';

import { EntityInfoPopover } from '../../EntityInfoPopover';
import { BranchLabel } from '../BranchLabel';
import { usePopoutTree } from '../context/PopoutTreeContext';
import { FloatingTreePanel } from '../FloatingTreePanel';
import type { DrillState } from '../types';
import { scrollTreeToTop } from './treeUtils';

export type Branch = {
  label: string;
  description?: string;
};

export type UnifiedPopoutTreeProps = Omit<
  TreeSelectionComponentProps,
  'drillMode' | 'onClick'
> &
  Omit<TreeApiBaseProps, 'openByDefault'> &
  TreeApiControlProps & {
    data: TreeData[];
    disabledLevels?: number[];
    height: number;
    drillMode: DrillMode;
    sortSuggestedSearch?: boolean;
    drillState: DrillState[];
    branches?: Branch[] | ((node: TNode) => Branch);
    isSearchMatch: (node: TNode, search: string) => boolean;
    isLoading?: boolean;
    onDrillStateChange: (level: number) => void;
  };

export const UnifiedPopoutTree = forwardRef(
  (
    {
      data,
      disabledLevels,
      height,
      drillMode,
      sortSuggestedSearch,
      drillState,
      branches,
      isSearchMatch,
      isLoading,
      onDrillStateChange,
      ...props
    }: UnifiedPopoutTreeProps,
    ref: Ref<
      | {
          scrollToTop: () => void;
        }
      | undefined
    >
  ) => {
    const { value = [] } = props;
    const popoutTreeContext = usePopoutTree();

    const _treeApi = useTreeApi({
      data,
      openByDefault: false,
      initialSelections: value,
      value,
      onChange: (ids, nodes) => {
        props.onChange?.(ids, nodes);
      },
    });

    const { treeRef, selectedIds } = _treeApi;

    const initialSelectedIds = useRef<string[]>(
      props.sortSelectedIds ?? selectedIds
    );

    useImperativeHandle(ref, () => {
      return {
        scrollToTop: () => scrollTreeToTop(treeRef),
      };
    });

    const sortedData = useMemo(() => {
      const markedIds = initialSelectedIds.current.flatMap((id) =>
        getPathToNode(id)
      );
      const isMarked = (id: string) => markedIds.includes(id);

      const sorted = [...data].sort((a, b) => {
        const aMarked = isMarked(a.id);
        const bMarked = isMarked(b.id);
        if (aMarked !== bMarked) {
          return aMarked ? -1 : 1;
        }

        const aIsOther = a.name === 'Other';
        const bIsOther = b.name === 'Other';
        if (aIsOther !== bIsOther) {
          return aIsOther ? 1 : -1;
        }

        return data.indexOf(a) - data.indexOf(b);
      });

      return sorted;
    }, [data]);

    return (
      <div>
        <TreeSelection
          {...props}
          data={sortedData}
          treeApi={_treeApi}
          openByDefault={false}
          disabled={disabledLevels}
          height={height}
          drillMode={drillMode}
          sortSuggestedSearch={sortSuggestedSearch}
          onClick={() => {
            onDrillStateChange(0);
          }}
          skipDefaultSort={isNil(popoutTreeContext)}
          popoverComponent={EntityInfoPopover}
        />
        {drillState.map(({ level, parentNode, anchorRowRect }, i) => {
          const children = getChildren({
            data: sortedData,
            idPath: getPathToNode(parentNode.id),
          });

          return (
            <FloatingTreePanel
              key={parentNode.id}
              anchorRowRect={anchorRowRect}
              popoutWidth={280}
              onClick={() => {
                onDrillStateChange(i + 1);
              }}
            >
              {Array.isArray(branches) && branches?.[i + 1] && (
                <BranchLabel
                  branch={branches?.[i + 1]?.label ?? ''}
                  branchDescription={branches?.[i + 1]?.description}
                />
              )}
              {typeof branches === 'function' && (
                <BranchLabel
                  branch={branches({ ...parentNode, level } as TNode).label}
                  branchDescription={
                    branches({ ...parentNode, level } as TNode)?.description
                  }
                />
              )}
              <TreeSelection
                {...props}
                data={children}
                treeApi={_treeApi}
                openByDefault={false}
                disabled={disabledLevels?.includes(i + 1)}
                searchMatch={isSearchMatch}
                drillMode={{
                  isEnabled: drillMode.isEnabled,
                  drillIntoNode: drillMode.drillIntoNode,
                  drillOutToLevel: drillMode.drillOutToLevel,
                  ...(drillMode.isEnabled
                    ? { isDrilledInto: drillMode.isDrilledInto }
                    : {}),
                  level: i + 1,
                }}
                isLoading={isLoading}
                popoverComponent={EntityInfoPopover}
              />
            </FloatingTreePanel>
          );
        })}
      </div>
    );
  }
);
